import { useEffect, useMemo, useRef, useState } from "react";
import { promoDiscount, useActivePromotion } from "@/hooks/useActivePromotion";
import { useNavigate } from "react-router-dom";
import { InstallAppBanner } from "@/hooks/usePWAManifest";
import {
  ShoppingBag, Plus, Minus, Trash2, Percent, DollarSign,
  CreditCard, X, Home, Send, FileText, Printer, Clock,
  Mail, Palette, EyeOff, Eye, Check, Bell, BellOff,
} from "lucide-react";
import { toast } from "sonner";
import { api, adminApi } from "@/lib/api";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import CustomizeModal from "@/components/CustomizeModal";
import { SquareRegisterSection } from "@/components/pos/SquareRegisterSection";
import { OnlineOrdersPanel, ONLINE_ACTIVE, ONLINE_NEXT_STATUS } from "@/components/pos/OnlineOrdersPanel";
import { CloseShiftButton } from "@/components/admin/CloseShiftButton";

const CUSTOMIZABLE = new Set([
  "Burgers",
  "Sandwiches",
  "Loaded Fries",
  "Vegetarian",
  "Wraps",
]);

// Platform detection for the Square POS deep-link hand-off.
// NOTE: iPadOS 13+ Safari defaults to "Request Desktop Website" and reports a
// Mac user-agent with NO "iPad" string. We treat a Mac UA that reports touch
// points (maxTouchPoints > 1) as an iPad so the Square reader hand-off fires.
function getSquarePlatform() {
  if (typeof navigator === "undefined") return { isAndroid: false, isIOS: false, isMobile: false };
  const ua = navigator.userAgent || "";
  let isAndroid = /Android/i.test(ua);
  const touchMac = /Mac/i.test(ua) && Number(navigator.maxTouchPoints) > 1;
  let isIOS = /iPhone|iPad|iPod/i.test(ua) || touchMac;
  // Manual override for the rare device that still misreports its platform.
  try {
    const forced = localStorage.getItem("bk_pos_force_tablet");
    if (forced === "ios") isIOS = true;
    else if (forced === "android") isAndroid = true;
  } catch { /* storage unavailable */ }
  return { isAndroid, isIOS, isMobile: isAndroid || isIOS };
}

// Build the Square POS deep link from the backend's sale-start response.
function buildSquareUrl(data, noteText, { isAndroid, isIOS }) {
  const { amount_cents: amt, callback_url: cb, state: st, application_id: appId } = data;
  if (isAndroid) {
    return [
      "intent:#Intent",
      "action=com.squareup.pos.action.CHARGE",
      "package=com.squareup",
      `S.com.squareup.pos.WEB_CALLBACK_URI=${encodeURIComponent(cb)}`,
      `S.com.squareup.pos.CLIENT_ID=${encodeURIComponent(appId)}`,
      "S.com.squareup.pos.API_VERSION=v2.0",
      `i.com.squareup.pos.TOTAL_AMOUNT=${amt}`,
      "S.com.squareup.pos.CURRENCY_CODE=USD",
      "S.com.squareup.pos.TENDER_TYPES=com.squareup.pos.TENDER_CARD",
      `S.com.squareup.pos.NOTE=${encodeURIComponent(noteText)}`,
      `S.com.squareup.pos.REQUEST_METADATA=${encodeURIComponent(st)}`,
      `S.browser_fallback_url=${encodeURIComponent(window.location.href)}`,
      "end",
    ].join(";");
  }
  if (isIOS) {
    const iosData = {
      amount_money: { amount: amt, currency_code: "USD" },
      callback_url: cb,
      client_id: appId,
      version: "1.3",
      notes: noteText,
      state: st,
      options: {
        supported_tender_types: ["CREDIT_CARD"],
        auto_return: true,
        clear_default_fees: false,
        skip_receipt: false,
      },
    };
    return "square-commerce-v1://payment/create?data=" + encodeURIComponent(JSON.stringify(iosData));
  }
  return "";
}

const TOKEN_KEY = "bk_admin_token";

// Colour palette inspired by Square POS category tiles. We hash each
// category name into this palette so the same name always lands on the
// same colour across sessions (and matches whatever the owner names their
// custom categories from Admin → Site Content).
const POS_TILE_PALETTE = [
  { bg: "#C0392B", ring: "#8B2A20" }, // brand red
  { bg: "#27AE60", ring: "#1E8449" }, // green
  { bg: "#F1C40F", ring: "#B7950B" }, // yellow
  { bg: "#8E44AD", ring: "#6C3483" }, // purple
  { bg: "#E67E22", ring: "#AF6109" }, // orange
  { bg: "#16A085", ring: "#0E6655" }, // teal
  { bg: "#2C3E50", ring: "#1B2631" }, // slate
  { bg: "#D81B60", ring: "#9B1250" }, // magenta
  { bg: "#3498DB", ring: "#21618C" }, // blue
];
function colourForCategory(name, overrides) {
  const override = overrides && overrides[name];
  if (override && /^#[0-9a-fA-F]{6}$/.test(override)) {
    // Darken 20% for the ring border so the button still reads as "pressed"
    // when active — cheap tint using a fixed alpha overlay.
    return { bg: override, ring: override };
  }
  const s = String(name || "");
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return POS_TILE_PALETTE[h % POS_TILE_PALETTE.length];
}

/**
 * PosPage — in-truck Point of Sale for tablet + phone.
 * Passcode-gated (shares the admin token). Cashier taps menu items to
 * build a sale, can add custom items on the fly, override any price,
 * apply a whole-order discount, and take a card via Square.
 */
export default function PosPage() {
  const navigate = useNavigate();
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [role, setRole] = useState(() => localStorage.getItem("bk_admin_role") || "");
  const [passcode, setPasscode] = useState("");
  const [showClock, setShowClock] = useState(false);
  const [menu, setMenu] = useState([]);
  const [activeCat, setActiveCat] = useState("");
  const [sale, setSale] = useState([]);            // [{ item_id, name, price, quantity, notes }]
  const [discountMode, setDiscountMode] = useState("dollar"); // "dollar" | "percent"
  const [discountAmount, setDiscountAmount] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [beeperNumber, setBeeperNumber] = useState("");
  const [showCustom, setShowCustom] = useState(false);
  const [customizeItem, setCustomizeItem] = useState(null);
  const [showPay, setShowPay] = useState(false);
  const [lastReceipt, setLastReceipt] = useState(null);
  const [showReport, setShowReport] = useState(false);
  const [todayGross, setTodayGross] = useState(0);
  // Online customer orders (channel !== "pos") currently in the kitchen
  // pipeline. The cashier can advance them Preparing → Ready → Picked up.
  const [onlineOrders, setOnlineOrders] = useState([]);
  const [showOnline, setShowOnline] = useState(false);
  const [advancingId, setAdvancingId] = useState(null);
  // New-online-order alert: chime + flashing "Online" badge, like the KDS.
  const [orderSound, setOrderSound] = useState(
    () => localStorage.getItem("bk_pos_order_sound") !== "off"
  );
  const [badgeFlash, setBadgeFlash] = useState(false);
  // Recently picked-up online orders (last 30 min) the cashier can pull back.
  const [pickedUp, setPickedUp] = useState([]);
  const seenOnlineRef = useRef(null); // Set of online order_numbers ever seen
  const audioCtxRef = useRef(null);
  const orderSoundRef = useRef(orderSound);
  orderSoundRef.current = orderSound;
  // Live tickets = every POS order currently in the kitchen pipeline.
  // Polled every 10s; the register renders one tab per ticket so the cashier
  // can peek at what's outstanding without switching to the KDS.
  const [liveTickets, setLiveTickets] = useState([]);
  const [squareCfg, setSquareCfg] = useState(null);
  useEffect(() => {
    if (!token) return;
    adminApi(token).get("/admin/pos/square-config").then((r) => setSquareCfg(r.data)).catch(() => {});
  }, [token]);
  // When set, the cart panel switches to a read-only view of that live
  // ticket. `null` = we're building a fresh order.
  const [viewingTicketId, setViewingTicketId] = useState(null);
  // Long-press tile editor state — { itemId } while open.
  const [tileEditor, setTileEditor] = useState(null);
  // After a paid sale, prompt the cashier to email the receipt to the guest.
  const [receiptEmailFor, setReceiptEmailFor] = useState(null);
  const { settings } = useSiteSettings();

  // Poll today's gross every 30 s so the cashier can glance at how the
  // day's going without opening the shift report.
  useEffect(() => {
    if (!token || role !== "admin") return;
    let cancelled = false;
    const pull = async () => {
      try {
        const { data } = await adminApi(token).get("/admin/pos/report");
        if (!cancelled) setTodayGross(data.gross || 0);
      } catch { /* transient */ }
    };
    pull();
    const t = setInterval(pull, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, [token, role]);

  // Poll live POS tickets every 10 s. When the kitchen marks an order
  // Completed/Cancelled it disappears from this list -> its tab pops off
  // the register automatically.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const pull = async () => {
      try {
        const { data } = await adminApi(token).get("/admin/pos/live-tickets");
        if (cancelled) return;
        setLiveTickets(data || []);
        // If we were watching a ticket that just closed, snap back to "New".
        setViewingTicketId((cur) =>
          cur && !(data || []).some((t) => t.id === cur) ? null : cur
        );
      } catch { /* transient */ }
    };
    pull();
    const t = setInterval(pull, 10000);
    return () => { cancelled = true; clearInterval(t); };
  }, [token]);

  // Poll open online orders every 5 s so the cashier gets a near-immediate
  // alert. Filtered to the live pipeline statuses.
  const pullOnline = useRef(null);

  // Loud 3-beep chime for a freshly-arrived online order (mirrors the KDS).
  const playOrderChime = () => {
    if (!orderSoundRef.current) return;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      if (!audioCtxRef.current) audioCtxRef.current = new AC();
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") ctx.resume();
      const now = ctx.currentTime;
      [
        { f: 1000, t: 0.0, gain: 0.6 },
        { f: 1000, t: 0.3, gain: 0.6 },
        { f: 1000, t: 0.6, gain: 0.6 },
        { f: 1320, t: 0.9, gain: 0.55 },
      ].forEach(({ f, t, gain }) => {
        const osc = ctx.createOscillator();
        const g = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = f;
        g.gain.setValueAtTime(0, now + t);
        g.gain.linearRampToValueAtTime(gain, now + t + 0.02);
        g.gain.exponentialRampToValueAtTime(0.001, now + t + 0.28);
        osc.connect(g).connect(ctx.destination);
        osc.start(now + t);
        osc.stop(now + t + 0.32);
      });
    } catch { /* audio unavailable */ }
  };

  const toggleOrderSound = () => {
    setOrderSound((v) => {
      const next = !v;
      localStorage.setItem("bk_pos_order_sound", next ? "on" : "off");
      orderSoundRef.current = next;
      if (next) playOrderChime();
      return next;
    });
  };

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const pull = async () => {
      try {
        const { data } = await adminApi(token).get("/orders");
        if (cancelled) return;
        const open = (data || [])
          .filter((o) => o.channel !== "pos" && ONLINE_ACTIVE.includes(o.status))
          .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        // Detect freshly-arrived online orders (Received, never seen) -> alert.
        const nums = open.map((o) => o.order_number);
        if (seenOnlineRef.current === null) {
          seenOnlineRef.current = new Set(nums); // seed silently on first load
        } else {
          const fresh = open.filter(
            (o) => o.status === "Received" && !seenOnlineRef.current.has(o.order_number)
          );
          if (fresh.length > 0) {
            playOrderChime();
            setBadgeFlash(true);
            setTimeout(() => setBadgeFlash(false), 8000);
            toast.info(`New online order${fresh.length > 1 ? "s" : ""}: ${fresh.map((o) => o.order_number).join(", ")}`);
          }
          nums.forEach((n) => seenOnlineRef.current.add(n));
        }
        setOnlineOrders(open);
      } catch { /* transient */ }
    };
    pullOnline.current = pull;
    pull();
    const t = setInterval(pull, 5000);
    return () => { cancelled = true; clearInterval(t); };
  }, [token]);

  const loadPickedUp = async () => {
    try {
      const { data } = await adminApi(token).get("/orders/recall-candidates", {
        params: { minutes: 30 },
      });
      setPickedUp((data || []).filter((o) => o.channel !== "pos"));
    } catch { /* transient */ }
  };

  const undoPickup = async (o) => {
    try {
      await adminApi(token).post(`/orders/${o.id}/recall`);
      toast.success(`${o.order_number} pulled back`);
      await Promise.all([pullOnline.current?.(), loadPickedUp()]);
    } catch {
      toast.error("Couldn't pull that order back");
    }
  };

  const markCurbsideServed = async (o) => {
    setAdvancingId(o.id);
    try {
      await adminApi(token).post(`/orders/${o.id}/curbside-delivered`);
      setOnlineOrders((cur) =>
        cur.map((x) =>
          x.id === o.id
            ? { ...x, curbside: { ...(x.curbside || {}), delivered_at: new Date().toISOString() } }
            : x
        )
      );
      toast.success(`${o.order_number} brought out to curbside`);
      pullOnline.current?.();
    } catch {
      toast.error("Couldn't mark curbside served");
    } finally {
      setAdvancingId(null);
    }
  };

  const advanceOnline = async (o) => {
    const next = ONLINE_NEXT_STATUS[o.status];
    if (!next) return;
    setAdvancingId(o.id);
    try {
      await adminApi(token).put(`/orders/${o.id}/status`, { status: next });
      setOnlineOrders((cur) =>
        next === "Completed"
          ? cur.filter((x) => x.id !== o.id)
          : cur.map((x) => (x.id === o.id ? { ...x, status: next } : x))
      );
      toast.success(next === "Completed" ? `${o.order_number} picked up` : `${o.order_number} → ${next}`);
      pullOnline.current?.();
      if (next === "Completed") loadPickedUp();
    } catch {
      toast.error("Couldn't update order");
    } finally {
      setAdvancingId(null);
    }
  };

  const CATEGORIES = useMemo(
    () =>
      (settings.menu_categories || []).filter((c) => c && c !== "Add-Ons"),
    [settings.menu_categories]
  );

  useEffect(() => {
    if (!activeCat && CATEGORIES.length) setActiveCat(CATEGORIES[0]);
  }, [CATEGORIES, activeCat]);

  // Load menu on auth, then refresh every 30s so items the owner adds or
  // 86's (from Admin, another tablet, or this POS) show up automatically —
  // the same /menu the online storefront reads, so changes stay in sync.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const pull = async () => {
      try {
        const { data } = await api.get("/menu");
        if (!cancelled) setMenu(data);
      } catch {
        /* transient — keep the last good menu */
      }
    };
    pull();
    const t = setInterval(pull, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/login", { passcode });
      localStorage.setItem(TOKEN_KEY, data.token);
      localStorage.setItem("bk_admin_role", data.role || "admin");
      setToken(data.token);
      setRole(data.role || "admin");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Wrong passcode");
    }
  };

  // Fetch the role for any pre-existing token (e.g. page reload) so we
  // consistently hide/show admin-only UI (Admin link + Today's Sales pill).
  useEffect(() => {
    if (!token || role) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await adminApi(token).get("/admin/me");
        if (!cancelled) {
          localStorage.setItem("bk_admin_role", data.role);
          setRole(data.role);
        }
      } catch {
        // token invalid — force re-login
        if (!cancelled) {
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem("bk_admin_role");
          setToken("");
          setRole("");
        }
      }
    })();
    return () => { cancelled = true; };
  }, [token, role]);

  const isAdmin = role === "admin";

  // ---- Sale math ----
  const subtotal = useMemo(
    () => sale.reduce((s, l) => s + l.price * l.quantity, 0),
    [sale]
  );
  const promo = useActivePromotion("pos");
  const promoOff = promoDiscount(subtotal, promo);
  const discountValue = useMemo(() => {
    const n = parseFloat(discountAmount);
    const base = Math.max(0, subtotal - promoOff);
    if (!n || isNaN(n) || n <= 0) return 0;
    if (discountMode === "percent") return Math.min(base, Math.round(base * n) / 100);
    return Math.min(base, n);
  }, [discountAmount, discountMode, subtotal, promoOff]);
  const taxable = Math.max(0, subtotal - promoOff - discountValue);
  const taxRate = settings.sales_tax_rate || 0;
  const tax = Math.round(taxable * taxRate * 100) / 100;
  const total = Math.round((taxable + tax) * 100) / 100;

  // ---- Sale mutations ----
  const addItem = (item) => {
    // Customizable categories open the same modal the customer app uses
    // (add-ons, ingredient removals). Non-customizable items go straight
    // in as before.
    if (CUSTOMIZABLE.has(item.category)) {
      setCustomizeItem(item);
      return;
    }
    setSale((s) => {
      const existing = s.findIndex(
        (l) => l.item_id === item.id && !l.notes && l.price === item.price
      );
      if (existing !== -1) {
        return s.map((l, i) =>
          i === existing ? { ...l, quantity: l.quantity + 1 } : l
        );
      }
      return [
        ...s,
        { item_id: item.id, name: item.name, price: item.price, quantity: 1, notes: "" },
      ];
    });
  };
  // Called by CustomizeModal via its onAdd prop.
  const addCustomizedItem = (item, mods) => {
    const noteParts = [];
    if (mods.removals?.length) noteParts.push(`No: ${mods.removals.join(", ")}`);
    if (mods.addOns?.length) {
      noteParts.push(`Add: ${mods.addOns.map((a) => a.name).join(", ")}`);
    }
    setSale((s) => [
      ...s,
      {
        item_id: item.id,
        name: item.name,
        price: mods.total,
        quantity: 1,
        notes: noteParts.join(" · "),
      },
    ]);
  };
  const addCustom = (name, price) => {
    setSale((s) => [
      ...s,
      {
        item_id: "",
        name: name.trim() || "Custom",
        price: parseFloat(price) || 0,
        quantity: 1,
        notes: "",
      },
    ]);
    setShowCustom(false);
  };
  const setLine = (idx, patch) =>
    setSale((s) => s.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  const bump = (idx, delta) =>
    setSale((s) =>
      s
        .map((l, i) => (i === idx ? { ...l, quantity: l.quantity + delta } : l))
        .filter((l) => l.quantity > 0)
    );
  const removeLine = (idx) => setSale((s) => s.filter((_, i) => i !== idx));
  const clearSale = () => {
    setSale([]);
    setDiscountAmount("");
    setCustomerName("");
    setBeeperNumber("");
  };

  // Pre-shift dry-run: open the Square POS app via the deep link so staff
  // can confirm the Bluetooth reader hand-off works, then CANCEL on the
  // Square screen. No pending sale is created and no signed state is sent,
  // so even an accidental completion can't book a local order.
  const testReader = () => {
    const { isAndroid, isIOS } = getSquarePlatform();
    if (!isAndroid && !isIOS) {
      toast.error("Open this on your iPad/Android tablet with the Square POS app installed");
      return;
    }
    const appId = squareCfg?.application_id || process.env.REACT_APP_SQUARE_APPLICATION_ID;
    if (!appId) {
      toast.error("Square Application ID isn't configured");
      return;
    }
    const cb = squareCfg?.callback_url || window.location.origin + "/api/pos/square-callback";
    const note = "Reader connection test — tap CANCEL on the Square screen";
    toast("Opening Square… tap Cancel on the Square screen to finish the test (no charge).", { duration: 7000 });
    if (isAndroid) {
      const parts = [
        "intent:#Intent",
        "action=com.squareup.pos.action.CHARGE",
        "package=com.squareup",
        `S.com.squareup.pos.WEB_CALLBACK_URI=${encodeURIComponent(cb)}`,
        `S.com.squareup.pos.CLIENT_ID=${encodeURIComponent(appId)}`,
        "S.com.squareup.pos.API_VERSION=v2.0",
        "i.com.squareup.pos.TOTAL_AMOUNT=100",
        "S.com.squareup.pos.CURRENCY_CODE=USD",
        "S.com.squareup.pos.TENDER_TYPES=com.squareup.pos.TENDER_CARD",
        `S.com.squareup.pos.NOTE=${encodeURIComponent(note)}`,
        `S.browser_fallback_url=${encodeURIComponent(window.location.href)}`,
        "end",
      ].join(";");
      window.location.href = parts;
    } else {
      const iosData = {
        amount_money: { amount: 100, currency_code: "USD" },
        callback_url: cb,
        client_id: appId,
        version: "1.3",
        notes: note,
        options: { supported_tender_types: ["CREDIT_CARD"] },
      };
      window.location.href =
        "square-commerce-v1://payment/create?data=" + encodeURIComponent(JSON.stringify(iosData));
    }
  };

  const gridItems = menu.filter(
    (m) =>
      m.category === activeCat &&
      m.category !== "Add-Ons" &&
      !m.pos_hidden
  );

  // Resolve the tile background for a specific menu item. Per-item override
  // wins first, then the owner's category color, then the deterministic
  // hash palette. Returns a { bg, ring } pair.
  const tileColor = (item) => {
    if (item?.pos_color && /^#[0-9a-fA-F]{6}$/.test(item.pos_color)) {
      return { bg: item.pos_color, ring: item.pos_color };
    }
    return colourForCategory(item.category, settings.menu_category_colors);
  };

  const posGridColumns = Math.min(6, Math.max(3, settings.pos_grid_columns || 4));
  const gridColsClass =
    posGridColumns === 3 ? "grid-cols-3" :
    posGridColumns === 5 ? "grid-cols-5" :
    posGridColumns === 6 ? "grid-cols-6" : "grid-cols-4";

  // ---- Long-press tile-editor persistence ----
  const saveTilePatch = async (itemId, patch) => {
    try {
      await adminApi(token).put(`/menu/${itemId}`, patch);
      // Optimistically refresh the local menu cache so the new color/hide
      // state renders instantly without waiting for a full refetch.
      setMenu((prev) =>
        prev.map((m) => (m.id === itemId ? { ...m, ...patch } : m))
      );
      toast.success("Tile updated");
    } catch {
      toast.error("Couldn't save tile change");
    }
  };

  const viewingTicket = liveTickets.find((t) => t.id === viewingTicketId) || null;

  // ---- Login gate ----
  if (!token) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-4">
        <InstallAppBanner
          manifest="/manifest-pos.json"
          themeColor="#C0392B"
          appleTitle="BK Register"
          label="BK Register (POS)"
        />
        <form onSubmit={login} className="w-full max-w-sm bg-white p-6 rounded-2xl border-2 border-brand-red shadow-lg">
          <h1 className="font-hand text-brand-red text-3xl text-center mb-4">
            POS Login
          </h1>
          <input
            type="password"
            value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
            placeholder="Staff passcode"
            data-testid="pos-passcode-input"
            className="w-full px-4 py-3 rounded-lg border border-stone-300 focus:border-brand-red outline-none text-center text-lg"
            autoFocus
          />
          <button
            type="submit"
            data-testid="pos-login-btn"
            className="mt-3 w-full bg-brand-red text-white py-3 rounded-lg font-bold uppercase tracking-widest text-sm hover:bg-red-800"
          >
            Enter Register
          </button>
          <div className="mt-4 pt-4 border-t border-stone-200 text-center">
            <button
              type="button"
              onClick={() => setShowClock(true)}
              data-testid="pos-open-clock-btn"
              className="text-brand-red text-xs uppercase tracking-widest font-bold hover:underline"
            >
              Clock in / out with PIN →
            </button>
          </div>
        </form>
        {showClock && <ClockDialog onClose={() => setShowClock(false)} />}
      </div>
    );
  }

  return (
    <div className="h-screen bg-[#FDFBF7] overflow-hidden flex flex-col" data-testid="pos-page">
      <InstallAppBanner
        manifest="/manifest-pos.json"
        themeColor="#C0392B"
        appleTitle="BK Register"
        label="BK Register (POS)"
      />

      {showClock && <ClockDialog onClose={() => setShowClock(false)} />}

      <div
        className="flex-1 flex flex-col md:flex-row overflow-hidden"
        data-testid="pos-workspace"
      >
        {/* ─── Left column: cart panel + ticket tabs ─── */}
        <div
          className="w-full md:w-[340px] lg:w-[380px] bg-white border-r border-stone-200 flex flex-col overflow-hidden shrink-0 max-h-[45vh] md:max-h-none"
          data-testid="pos-cart-panel"
        >
          {/* Ticket tab strip — always shows "New" first, then each in-flight POS ticket */}
          <div
            className="flex gap-1 overflow-x-auto no-scrollbar border-b border-stone-200 bg-stone-50 px-2 pt-2"
            data-testid="pos-ticket-tabs"
          >
            <button
              onClick={() => setViewingTicketId(null)}
              data-testid="pos-ticket-tab-new"
              className={`shrink-0 px-3 py-1.5 rounded-t-md text-[11px] font-black uppercase tracking-widest flex items-center gap-1 ${
                viewingTicketId === null
                  ? "bg-white text-brand-red border-t-2 border-x-2 border-brand-red -mb-px"
                  : "bg-stone-200 text-stone-600 hover:bg-stone-300"
              }`}
            >
              <Plus className="w-3 h-3" /> New
            </button>
            {liveTickets.map((t, idx) => {
              const active = viewingTicketId === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setViewingTicketId(t.id)}
                  data-testid={`pos-ticket-tab-${t.id}`}
                  title={`${t.customer_name || "Guest"} · ${t.status}`}
                  className={`shrink-0 px-3 py-1.5 rounded-t-md text-[11px] font-black uppercase tracking-widest flex items-center gap-1.5 ${
                    active
                      ? "bg-white text-brand-red border-t-2 border-x-2 border-brand-red -mb-px"
                      : "bg-stone-200 text-stone-700 hover:bg-stone-300"
                  }`}
                >
                  <span className="w-5 h-5 rounded-full bg-brand-yellow text-brand-red flex items-center justify-center text-[10px] font-black">
                    {idx + 1}
                  </span>
                  {t.beeper_number || t.customer_name?.slice(0, 8) || `#${(t.order_number || "").slice(-3)}`}
                </button>
              );
            })}
          </div>

          {viewingTicket ? (
            /* Read-only view of a live in-flight ticket */
            <LiveTicketView ticket={viewingTicket} onBack={() => setViewingTicketId(null)} />
          ) : (
            <>
              {/* Customer + beeper header */}
              <div className="p-2.5 border-b border-stone-200 grid grid-cols-3 gap-2">
                <input
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="Customer name (optional)"
                  data-testid="pos-customer-name"
                  className="col-span-2 px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-red outline-none text-sm"
                />
                <input
                  value={beeperNumber}
                  onChange={(e) => setBeeperNumber(e.target.value)}
                  placeholder="Beeper #"
                  data-testid="pos-beeper-number"
                  maxLength={8}
                  className="px-3 py-2 rounded-lg border border-brand-red focus:bg-yellow-50 outline-none text-sm font-mono font-black text-center"
                />
              </div>

              {/* Line items scroll region */}
              <div
                className="flex-1 overflow-y-auto p-2.5 space-y-1.5"
                data-testid="pos-sale-lines"
                style={{ minHeight: 180 }}
              >
                {sale.length === 0 ? (
                  <div className="text-center text-stone-400 py-8 text-sm">
                    <ShoppingBag className="w-8 h-8 mx-auto mb-2 text-stone-300" />
                    Tap items to start a sale
                  </div>
                ) : (
                  sale.map((l, i) => (
                    <div
                      key={i}
                      data-testid={`pos-sale-line-${i}`}
                      className="rounded-lg border border-stone-200 bg-stone-50 p-2 space-y-1"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-stone-800 text-sm truncate">
                            {l.name}
                          </div>
                          {l.notes && (
                            <div className="text-[10px] text-stone-500 truncate italic">
                              {l.notes}
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => removeLine(i)}
                          data-testid={`pos-remove-line-${i}`}
                          className="text-stone-400 hover:text-brand-red p-1"
                          aria-label={`Remove ${l.name}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => bump(i, -1)}
                          className="w-7 h-7 rounded-full bg-white border border-stone-300 text-stone-700 flex items-center justify-center"
                          aria-label="Decrease quantity"
                        >
                          <Minus className="w-3.5 h-3.5" />
                        </button>
                        <span className="font-mono font-bold w-5 text-center text-sm">
                          {l.quantity}
                        </span>
                        <button
                          onClick={() => bump(i, +1)}
                          data-testid={`pos-plus-line-${i}`}
                          className="w-7 h-7 rounded-full bg-white border border-stone-300 text-stone-700 flex items-center justify-center"
                          aria-label="Increase quantity"
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={l.price}
                          onChange={(e) =>
                            setLine(i, {
                              price: parseFloat(e.target.value) || 0,
                            })
                          }
                          data-testid={`pos-price-line-${i}`}
                          className="w-16 ml-auto px-1 py-1 rounded border border-stone-300 text-xs text-right font-mono"
                        />
                        <span className="font-bold text-stone-800 w-14 text-right font-mono text-sm">
                          ${(l.price * l.quantity).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Discount + Totals + action bar */}
              <div className="border-t border-stone-200 p-2.5 space-y-1.5 text-sm">
                <div className="flex items-center gap-2">
                  <div className="flex bg-stone-100 rounded-full p-0.5">
                    <button
                      onClick={() => setDiscountMode("dollar")}
                      className={`px-2 py-1 rounded-full text-xs font-bold ${
                        discountMode === "dollar"
                          ? "bg-white shadow"
                          : "text-stone-500"
                      }`}
                      aria-label="Dollar discount"
                    >
                      <DollarSign className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => setDiscountMode("percent")}
                      className={`px-2 py-1 rounded-full text-xs font-bold ${
                        discountMode === "percent"
                          ? "bg-white shadow"
                          : "text-stone-500"
                      }`}
                      aria-label="Percent discount"
                    >
                      <Percent className="w-3 h-3" />
                    </button>
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={discountAmount}
                    onChange={(e) => setDiscountAmount(e.target.value)}
                    placeholder={discountMode === "percent" ? "% off" : "$ off"}
                    data-testid="pos-discount-input"
                    className="flex-1 px-3 py-1.5 rounded border border-stone-300 text-sm outline-none focus:border-brand-red"
                  />
                  {discountValue > 0 && (
                    <span className="text-brand-red font-bold font-mono text-xs">
                      −${discountValue.toFixed(2)}
                    </span>
                  )}
                </div>

                <div className="flex justify-between text-stone-600 text-xs">
                  <span>Subtotal</span>
                  <span className="font-mono">${subtotal.toFixed(2)}</span>
                </div>
                {promoOff > 0 && (
                  <div className="flex justify-between text-emerald-700 text-xs font-bold" data-testid="pos-promo-line">
                    <span>{promo.name} · {promo.percent}% off</span>
                    <span className="font-mono">−${promoOff.toFixed(2)}</span>
                  </div>
                )}
                {discountValue > 0 && (
                  <div className="flex justify-between text-brand-red text-xs">
                    <span>Discount</span>
                    <span className="font-mono">−${discountValue.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-stone-600 text-xs">
                  <span>Tax ({(taxRate * 100).toFixed(2)}%)</span>
                  <span className="font-mono">${tax.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold text-stone-800 border-t border-stone-200 pt-1.5">
                  <span>Total</span>
                  <span className="font-mono" data-testid="pos-total">
                    ${total.toFixed(2)}
                  </span>
                </div>

                <div className="flex gap-1.5 pt-1">
                  <button
                    onClick={clearSale}
                    disabled={sale.length === 0}
                    data-testid="pos-clear-btn"
                    className="py-2.5 px-3 rounded-lg border border-stone-300 text-stone-700 font-bold text-[11px] uppercase tracking-widest disabled:opacity-40 hover:bg-stone-100"
                  >
                    Clear
                  </button>
                  <button
                    onClick={() => setShowPay(true)}
                    disabled={sale.length === 0 || total <= 0}
                    data-testid="pos-charge-btn"
                    className="flex-1 py-2.5 rounded-lg bg-brand-red text-white font-black text-xs uppercase tracking-widest disabled:opacity-40 hover:bg-red-800 flex items-center justify-center gap-1.5"
                  >
                    <CreditCard className="w-4 h-4" />
                    Pay ${total.toFixed(2)}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* ─── Middle column: category rail (vertical on tablet, horizontal on phone) ─── */}
        <div
          className="flex md:flex-col gap-1.5 p-2 bg-stone-900 md:w-24 lg:w-28 overflow-x-auto md:overflow-x-visible md:overflow-y-auto no-scrollbar shrink-0"
          data-testid="pos-category-rail"
        >
          {CATEGORIES.map((c) => {
            const col = colourForCategory(c, settings.menu_category_colors);
            const active = activeCat === c;
            return (
              <button
                key={c}
                onClick={() => setActiveCat(c)}
                data-testid={`pos-cat-${c}`}
                style={{
                  backgroundColor: active ? col.bg : "#ffffff",
                  color: active ? "#ffffff" : col.bg,
                  borderColor: col.ring,
                }}
                className="shrink-0 md:shrink px-2 py-3 rounded-md text-[10px] uppercase tracking-widest font-black border-2 whitespace-nowrap md:whitespace-normal text-center leading-tight min-w-[92px] md:min-w-0 md:min-h-[64px] transition-transform active:scale-95"
              >
                {c}
              </button>
            );
          })}
          <button
            onClick={() => setShowCustom(true)}
            data-testid="pos-custom-item-btn"
            className="shrink-0 lg:shrink px-2 py-3 rounded-md text-[10px] uppercase tracking-widest font-black border-2 border-brand-yellow bg-brand-yellow text-brand-red flex items-center justify-center gap-1 min-w-[92px] lg:min-w-0 lg:min-h-[64px] active:scale-95"
          >
            <Plus className="w-3.5 h-3.5" /> Custom
          </button>
        </div>

        {/* ─── Right column: product grid ─── */}
        <div className="flex-1 flex flex-col bg-stone-100 overflow-hidden min-w-0">
          {/* Compact register header — clock/report/admin/logout live here */}
          <div className="chalkboard-band px-3 py-2 flex items-center justify-between gap-2 flex-wrap shrink-0">
            <div className="flex items-center gap-2">
              <div className="text-brand-yellow font-display text-lg leading-none">
                Register
              </div>
              {isAdmin ? (
                <div
                  data-testid="pos-today-gross"
                  title="Today's total sales (UTC)"
                  className="bg-brand-yellow text-brand-red px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest font-mono"
                >
                  Today: ${todayGross.toFixed(2)}
                </div>
              ) : (
                <div
                  data-testid="pos-role-cashier"
                  className="bg-stone-600 text-brand-yellow px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest"
                >
                  Cashier
                </div>
              )}
              <div
                data-testid="pos-live-count"
                className="bg-emerald-600 text-white px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest"
                title="Open tickets in the kitchen"
              >
                {liveTickets.length} open
              </div>
            </div>
            <div className="flex items-center gap-1 flex-wrap">
              <button
                onClick={() => setShowClock(true)}
                data-testid="pos-open-clock-btn-header"
                title="Clock in / out"
                className="text-brand-yellow hover:text-yellow-200 text-[11px] uppercase tracking-widest font-bold flex items-center gap-1 px-2 py-1"
              >
                <Clock className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Clock</span>
              </button>
              <button
                onClick={testReader}
                data-testid="pos-test-reader-btn"
                title="Test the Square reader hand-off (no charge)"
                className="text-brand-yellow hover:text-yellow-200 text-[11px] uppercase tracking-widest font-bold flex items-center gap-1 px-2 py-1"
              >
                <CreditCard className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Test reader</span>
              </button>
              <button
                onClick={() => { setShowOnline(true); setBadgeFlash(false); loadPickedUp(); }}
                data-testid="pos-online-orders-btn"
                title="Online customer orders"
                className={`relative text-brand-yellow hover:text-yellow-200 text-[11px] uppercase tracking-widest font-bold flex items-center gap-1 px-2 py-1 rounded ${badgeFlash ? "ring-2 ring-emerald-400 animate-pulse bg-emerald-600/20" : ""}`}
              >
                <ShoppingBag className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Online</span>
                {onlineOrders.length > 0 && (
                  <span
                    data-testid="pos-online-badge"
                    className={`ml-0.5 text-white rounded-full min-w-[18px] h-[18px] px-1 text-[10px] font-black flex items-center justify-center ${badgeFlash ? "bg-red-500 animate-pulse" : "bg-emerald-500"}`}
                  >
                    {onlineOrders.length}
                  </span>
                )}
              </button>
              <button
                onClick={toggleOrderSound}
                data-testid="pos-order-sound-toggle"
                title={orderSound ? "Mute new-order chime" : "Unmute new-order chime"}
                className="text-brand-yellow hover:text-yellow-200 px-1.5 py-1"
              >
                {orderSound ? <Bell className="w-3.5 h-3.5" /> : <BellOff className="w-3.5 h-3.5" />}
              </button>
              {isAdmin && (
                <button
                  onClick={() => setShowReport(true)}
                  data-testid="pos-report-btn"
                  title="Shift report"
                  className="text-white/80 hover:text-white text-[11px] uppercase tracking-widest font-bold flex items-center gap-1 px-2 py-1"
                >
                  <FileText className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Report</span>
                </button>
              )}
              {isAdmin && (
                <button
                  onClick={() => navigate("/admin")}
                  data-testid="pos-admin-link"
                  title="Admin"
                  className="text-white/80 hover:text-white text-[11px] uppercase tracking-widest font-bold flex items-center gap-1 px-2 py-1"
                >
                  <Home className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Admin</span>
                </button>
              )}
              <button
                onClick={() => {
                  adminApi(token).post("/admin/logout").catch(() => {});
                  localStorage.removeItem(TOKEN_KEY);
                  localStorage.removeItem("bk_admin_role");
                  setToken("");
                  setRole("");
                }}
                data-testid="pos-logout-btn"
                title="Log out"
                className="text-white/80 hover:text-white text-[11px] uppercase tracking-widest font-bold px-2 py-1"
              >
                Log out
              </button>
            </div>
          </div>

          <div
            className={`flex-1 p-2.5 grid ${gridColsClass} gap-2 overflow-y-auto content-start`}
            data-testid="pos-item-grid"
          >
            {gridItems.map((item) => (
              <PosTile
                key={item.id}
                item={item}
                col={tileColor(item)}
                disabled={item.available === false}
                canEdit={true}
                onTap={() => addItem(item)}
                onLongPress={() => setTileEditor({ itemId: item.id })}
              />
            ))}
            {gridItems.length === 0 && (
              <div className="col-span-full text-center text-stone-400 py-12 text-sm">
                No items in {activeCat}
              </div>
            )}
          </div>
          {isAdmin ? (
            <div className="px-3 py-1.5 border-t border-stone-200 bg-white text-[10px] uppercase tracking-widest text-stone-500 text-center shrink-0">
              Long-press a tile to mark it out of stock, change its color, or hide it
            </div>
          ) : (
            <div className="px-3 py-1.5 border-t border-stone-200 bg-white text-[10px] uppercase tracking-widest text-stone-500 text-center shrink-0">
              Long-press a tile to mark it out of stock
            </div>
          )}
        </div>
      </div>

      {tileEditor && (
        <TileEditorSheet
          item={menu.find((m) => m.id === tileEditor.itemId)}
          currentColor={
            (menu.find((m) => m.id === tileEditor.itemId) || {}).pos_color
          }
          canEditTile={isAdmin}
          onSave={async (patch) => {
            await saveTilePatch(tileEditor.itemId, patch);
            setTileEditor(null);
          }}
          onClose={() => setTileEditor(null)}
        />
      )}

      {showCustom && (
        <CustomItemDialog
          onCancel={() => setShowCustom(false)}
          onAdd={addCustom}
        />
      )}

      {customizeItem && (
        <CustomizeModal
          item={customizeItem}
          onClose={() => setCustomizeItem(null)}
          onAdd={addCustomizedItem}
        />
      )}

      {showPay && (
        <PayDialog
          token={token}
          total={total}
          payload={{
            lines: sale,
            discount_amount: parseFloat(discountAmount) || 0,
            discount_mode: discountMode,
            customer_name: customerName,
            beeper_number: beeperNumber,
            send_to_kitchen: true,
          }}
          onClose={() => setShowPay(false)}
          onSuccess={(receipt) => {
            setShowPay(false);
            setLastReceipt(receipt);
            // Offer to email the customer their receipt now that the sale
            // is booked. Card sales come back with just `order_number`
            // (from the Square callback); we look up the order server-side
            // in the /admin/pos/receipt/email endpoint.
            if (receipt?.order_number || receipt?.order_id) {
              setReceiptEmailFor({
                orderId: receipt.order_id || null,
                orderNumber: receipt.order_number || null,
                total: receipt.total,
              });
            }
            clearSale();
          }}
        />
      )}

      {receiptEmailFor && (
        <ReceiptEmailDialog
          token={token}
          orderId={receiptEmailFor.orderId}
          orderNumber={receiptEmailFor.orderNumber}
          total={receiptEmailFor.total}
          onClose={() => setReceiptEmailFor(null)}
        />
      )}

      {lastReceipt && (
        <ReceiptDialog
          receipt={lastReceipt}
          onClose={() => setLastReceipt(null)}
        />
      )}

      {showReport && (
        <ShiftReportDialog token={token} onClose={() => setShowReport(false)} />
      )}
      {showOnline && (
        <OnlineOrdersPanel
          orders={onlineOrders}
          busyId={advancingId}
          onAdvance={advanceOnline}
          onCurbsideServed={markCurbsideServed}
          pickedUp={pickedUp}
          onUndoPickup={undoPickup}
          onClose={() => setShowOnline(false)}
        />
      )}
    </div>
  );
}

/**
 * A single register tile. Tap = add to cart. Long-press (~550 ms) = open
 * the tile editor so an admin can change its color or hide it from the
 * POS. The long-press is skipped for non-admins.
 */
function PosTile({ item, col, disabled, canEdit, onTap, onLongPress }) {
  const timerRef = useRef(null);
  const longFiredRef = useRef(false);

  const start = () => {
    if (!canEdit) return;
    longFiredRef.current = false;
    timerRef.current = setTimeout(() => {
      longFiredRef.current = true;
      onLongPress();
    }, 550);
  };
  const cancel = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };
  const handleClick = () => {
    if (longFiredRef.current) return;
    if (disabled) {
      toast("Sold out — long-press the tile to bring it back");
      return;
    }
    onTap();
  };

  return (
    <button
      onClick={handleClick}
      onPointerDown={start}
      onPointerUp={cancel}
      onPointerLeave={cancel}
      onPointerCancel={cancel}
      onContextMenu={(e) => {
        if (canEdit) e.preventDefault();
      }}
      data-testid={`pos-item-${item.id}`}
      style={{ backgroundColor: col.bg }}
      className={`relative rounded-xl border-2 border-transparent hover:-translate-y-0.5 transition-all p-2.5 text-left text-white shadow-md min-h-[84px] flex flex-col justify-between select-none ${disabled ? "opacity-70" : ""}`}
    >
      <div className="font-black text-[13px] leading-tight text-white drop-shadow-sm line-clamp-3">
        {item.name}
      </div>
      <div className="font-mono text-white/95 text-[13px] font-bold mt-1">
        ${item.price.toFixed(2)}
      </div>
      {item.pos_hidden && (
        <span className="absolute top-1 right-1 bg-black/50 text-white text-[9px] px-1.5 py-0.5 rounded uppercase tracking-widest">
          hidden
        </span>
      )}
      {disabled && (
        <span
          data-testid={`pos-soldout-${item.id}`}
          className="absolute inset-0 bg-black/55 rounded-xl flex items-center justify-center"
        >
          <span className="bg-white text-stone-900 text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded -rotate-6">
            Sold out
          </span>
        </span>
      )}
    </button>
  );
}


/**
 * Read-only preview of a live POS ticket. Cashier opens this from the
 * ticket-tabs strip to check what's in-flight for a given guest without
 * touching the KDS.
 */
function LiveTicketView({ ticket, onBack }) {
  const items = ticket.items || [];
  const total = Number(ticket.total || 0);
  const subtotal = Number(ticket.subtotal || 0);
  const tax = Number(ticket.tax || 0);
  return (
    <div className="flex-1 flex flex-col" data-testid={`pos-ticket-view-${ticket.id}`}>
      <div className="p-3 border-b border-stone-200 flex items-center justify-between gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-stone-500">
            Live ticket
          </div>
          <div className="font-display text-brand-red text-xl leading-none mt-0.5">
            {ticket.beeper_number
              ? `Beeper ${ticket.beeper_number}`
              : ticket.customer_name || `#${(ticket.order_number || "").slice(-4)}`}
          </div>
          <div className="text-[11px] text-stone-500 mt-1">
            Status: <span className="font-bold text-stone-700">{ticket.status}</span>
          </div>
        </div>
        <button
          onClick={onBack}
          data-testid="pos-ticket-view-back"
          className="text-brand-red text-[11px] uppercase tracking-widest font-bold flex items-center gap-1 px-2 py-1 rounded-full border border-brand-red hover:bg-brand-red hover:text-white"
        >
          <Plus className="w-3 h-3" /> New sale
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-2.5 space-y-1.5">
        {items.map((it, i) => (
          <div
            key={i}
            className="rounded-lg border border-stone-200 bg-stone-50 p-2 flex justify-between items-start gap-2"
          >
            <div className="min-w-0">
              <div className="font-bold text-stone-800 text-sm">
                {it.quantity}× {it.name}
              </div>
              {it.modifications && (
                <div className="text-[10px] text-stone-500 italic mt-0.5">
                  {it.modifications}
                </div>
              )}
            </div>
            <div className="font-mono text-stone-700 text-sm">
              ${(Number(it.price || 0) * Number(it.quantity || 1)).toFixed(2)}
            </div>
          </div>
        ))}
      </div>
      <div className="border-t border-stone-200 p-3 space-y-1 text-sm">
        <div className="flex justify-between text-stone-600 text-xs">
          <span>Subtotal</span>
          <span className="font-mono">${subtotal.toFixed(2)}</span>
        </div>
        <div className="flex justify-between text-stone-600 text-xs">
          <span>Tax</span>
          <span className="font-mono">${tax.toFixed(2)}</span>
        </div>
        <div className="flex justify-between font-black text-stone-800 pt-1 border-t border-stone-200">
          <span>Total</span>
          <span className="font-mono">${total.toFixed(2)}</span>
        </div>
      </div>
    </div>
  );
}


/**
 * Long-press editor sheet. Two controls: color picker (palette + free hex)
 * and a "hide from POS" toggle. Saves via /menu/{id} PUT and closes.
 */
const TILE_PALETTE = [
  "#C0392B", "#E67E22", "#F1C40F", "#27AE60",
  "#16A085", "#3498DB", "#8E44AD", "#D81B60",
  "#2C3E50", "#7F8C8D",
];

function TileEditorSheet({ item, currentColor, canEditTile = true, onSave, onClose }) {
  const [color, setColor] = useState(currentColor || "");
  const [hidden, setHidden] = useState(!!item?.pos_hidden);
  const [soldOut, setSoldOut] = useState(item?.available === false);
  const [saving, setSaving] = useState(false);
  if (!item) return null;
  const save = async () => {
    setSaving(true);
    const patch = { available: !soldOut };
    if (canEditTile) {
      patch.pos_color = color && /^#[0-9a-fA-F]{6}$/.test(color) ? color : null;
      patch.pos_hidden = hidden;
    }
    try {
      await onSave(patch);
    } finally {
      setSaving(false);
    }
  };
  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-sm p-4 space-y-4"
        onClick={(e) => e.stopPropagation()}
        data-testid="pos-tile-editor"
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-stone-500">
              Edit tile
            </div>
            <h2 className="font-display text-brand-red text-xl leading-none mt-0.5">
              {item.name}
            </h2>
          </div>
          <button onClick={onClose} aria-label="Close">
            <X className="w-5 h-5 text-stone-500" />
          </button>
        </div>

        {/* Out of stock — available to cashiers AND admins. Syncs to the
            online storefront (shows "Sold out") via the shared menu. */}
        <label className="flex items-center gap-3 bg-stone-50 rounded-lg border border-stone-200 px-3 py-2.5 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={soldOut}
            onChange={(e) => setSoldOut(e.target.checked)}
            data-testid="pos-tile-soldout-toggle"
            className="w-4 h-4 accent-brand-red"
          />
          <span className="text-sm text-stone-700 flex items-center gap-1.5">
            {soldOut ? (
              <EyeOff className="w-4 h-4 text-brand-red" />
            ) : (
              <Check className="w-4 h-4 text-emerald-600" />
            )}
            <span>
              <span className="font-bold">Out of stock</span>
              <span className="block text-[11px] text-stone-500">
                Also shows “Sold out” on the online menu
              </span>
            </span>
          </span>
        </label>

        {canEditTile && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-stone-600 font-black mb-1.5 flex items-center gap-1">
            <Palette className="w-3 h-3" /> Tile color
          </div>
          <div className="flex flex-wrap gap-1.5">
            <button
              onClick={() => setColor("")}
              data-testid="pos-tile-color-default"
              className={`w-9 h-9 rounded-lg border-2 bg-white text-[9px] font-black flex items-center justify-center ${
                !color ? "border-brand-red" : "border-stone-300"
              }`}
              title="Use category color"
            >
              Auto
            </button>
            {TILE_PALETTE.map((c) => (
              <button
                key={c}
                onClick={() => setColor(c)}
                style={{ backgroundColor: c }}
                data-testid={`pos-tile-color-${c}`}
                className={`w-9 h-9 rounded-lg border-2 ${
                  color === c ? "border-black" : "border-white/30"
                }`}
                title={c}
              >
                {color === c && <Check className="w-4 h-4 mx-auto text-white drop-shadow" />}
              </button>
            ))}
          </div>
          <input
            type="text"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            placeholder="#rrggbb (advanced)"
            data-testid="pos-tile-color-hex"
            className="mt-2 w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-red outline-none text-sm font-mono"
          />
        </div>
        )}

        {canEditTile && (
        <label className="flex items-center gap-3 bg-stone-50 rounded-lg border border-stone-200 px-3 py-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={hidden}
            onChange={(e) => setHidden(e.target.checked)}
            data-testid="pos-tile-hide-toggle"
            className="w-4 h-4 accent-brand-red"
          />
          <span className="text-sm text-stone-700 flex items-center gap-1.5">
            {hidden ? (
              <EyeOff className="w-4 h-4 text-stone-500" />
            ) : (
              <Eye className="w-4 h-4 text-stone-500" />
            )}
            Hide this tile from the register
          </span>
        </label>
        )}

        <div className="flex gap-2 pt-1">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-lg border border-stone-300 text-stone-700 font-bold text-xs uppercase tracking-widest hover:bg-stone-100"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="pos-tile-save"
            className="flex-1 py-2.5 rounded-lg bg-brand-red text-white font-black text-xs uppercase tracking-widest hover:bg-red-800 disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}


/**
 * Post-sale prompt: "Email your customer their receipt?" Skips itself if
 * the cashier taps Close. Uses /admin/pos/receipt/email server-side.
 */
function ReceiptEmailDialog({ token, orderId, orderNumber, total, onClose }) {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const send = async () => {
    if (!email.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    setSending(true);
    try {
      await adminApi(token).post("/admin/pos/receipt/email", {
        order_id: orderId || undefined,
        order_number: orderNumber || undefined,
        email: email.trim(),
      });
      toast.success("Receipt emailed");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send receipt");
    } finally {
      setSending(false);
    }
  };
  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-sm p-4 space-y-3"
        onClick={(e) => e.stopPropagation()}
        data-testid="pos-receipt-email-dialog"
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-stone-500">
              Paid ${Number(total || 0).toFixed(2)}
            </div>
            <h2 className="font-display text-brand-red text-xl leading-none mt-0.5 flex items-center gap-2">
              <Mail className="w-5 h-5" /> Email receipt?
            </h2>
          </div>
          <button onClick={onClose} aria-label="Skip">
            <X className="w-5 h-5 text-stone-500" />
          </button>
        </div>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="customer@email.com"
          data-testid="pos-receipt-email-input"
          autoFocus
          className="w-full px-3 py-3 rounded-lg border border-stone-300 focus:border-brand-red outline-none text-base"
        />
        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-lg border border-stone-300 text-stone-700 font-bold text-xs uppercase tracking-widest hover:bg-stone-100"
          >
            Skip
          </button>
          <button
            onClick={send}
            disabled={sending || !email}
            data-testid="pos-receipt-email-send"
            className="flex-[2] py-2.5 rounded-lg bg-brand-red text-white font-black text-xs uppercase tracking-widest hover:bg-red-800 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <Mail className="w-4 h-4" />
            {sending ? "Sending..." : "Send receipt"}
          </button>
        </div>
      </div>
    </div>
  );
}




function CustomItemDialog({ onCancel, onAdd }) {
  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-sm p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-brand-red text-xl">Custom item</h2>
          <button onClick={onCancel}><X className="w-5 h-5 text-stone-500" /></button>
        </div>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Item name"
          data-testid="pos-custom-name"
          className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-red outline-none text-sm"
          autoFocus
        />
        <input
          type="number"
          step="0.01"
          min="0"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          placeholder="Price ($)"
          data-testid="pos-custom-price"
          className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-red outline-none text-sm font-mono"
        />
        <button
          onClick={() => name && price && onAdd(name, price)}
          disabled={!name || !price}
          data-testid="pos-custom-add-btn"
          className="w-full py-2 rounded-lg bg-brand-red text-white font-bold text-xs uppercase tracking-widest disabled:opacity-40"
        >
          Add to sale
        </button>
      </div>
    </div>
  );
}

function SquareSetupInfo({ token }) {
  const [cfg, setCfg] = useState(null);
  useEffect(() => {
    adminApi(token).get("/admin/pos/square-config").then((r) => setCfg(r.data)).catch(() => setCfg({}));
  }, [token]);
  if (!cfg) return <div className="mt-2">Loading…</div>;
  const copy = (txt) => navigator.clipboard?.writeText(txt).then(() => toast.success("Copied"));
  return (
    <div className="mt-2 space-y-2">
      <div>
        In the <b>Square Developer Dashboard → your app → Point of Sale API → iOS → Web Callback URLs</b>, add exactly:
      </div>
      <div className="flex items-center gap-2">
        <code className="flex-1 break-all rounded bg-white border border-stone-200 px-2 py-1 text-stone-800" data-testid="pos-square-callback-url">
          {cfg.callback_url || "— not configured —"}
        </code>
        <button type="button" onClick={() => copy(cfg.callback_url || "")} data-testid="pos-square-callback-copy"
          className="shrink-0 px-2 py-1 rounded bg-stone-900 text-brand-yellow font-black uppercase tracking-widest">
          Copy
        </button>
      </div>
      <div>
        Application ID: <code className="break-all" data-testid="pos-square-app-id">{cfg.application_id || "—"}</code>
        {cfg.environment ? <> · {cfg.environment}</> : null}
      </div>
    </div>
  );
}

function PayDialog({ token, total, payload, onClose, onSuccess }) {
  const [starting, setStarting] = useState(false);
  const [saleId, setSaleId] = useState(null);
  const [error, setError] = useState("");
  const [polling, setPolling] = useState(false);
  const [squareUrl, setSquareUrl] = useState("");
  const [squareOpened, setSquareOpened] = useState(false);

  // iOS Safari only lets JS open another app's URL scheme while the user's tap
  // is still "active". If the page never went to the background after our
  // automatic attempt, the hand-off was silently blocked — surface a manual button.
  useEffect(() => {
    if (!polling) return;
    const onHide = () => { if (document.visibilityState === "hidden") setSquareOpened(true); };
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", onHide);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", onHide);
    };
  }, [polling]);
  // Payment method: Square Bluetooth reader (card) or cash.
  const [method, setMethod] = useState("card");
  const [tenderedStr, setTenderedStr] = useState("");
  const [cashSaving, setCashSaving] = useState(false);
  // Tip flow: three modes.
  //   "tablet"  = show a big customer-facing tip screen on this tablet;
  //               cashier hands device to the customer, they pick a %,
  //               then we bake it into the amount and launch Square.
  //   "reader"  = send subtotal+tax only; Square Register asks on the
  //               paired Bluetooth reader (merchant must enable in Square
  //               Register → Settings → Tips → Chip & contactless reader).
  //   "manual"  = cashier types the tip $ themselves — baked in.
  const [tipMode, setTipMode] = useState("tablet");
  const [manualTipStr, setManualTipStr] = useState("");
  // Tablet-mode workflow state: tabletTipCents is null until the customer
  // picks something on the CustomerTipScreen; showTabletTip is a separate
  // boolean that controls whether the full-screen picker is currently
  // rendered (so tabletTipCents stays a clean number|null throughout).
  const [tabletTipCents, setTabletTipCents] = useState(null);
  const [showTabletTip, setShowTabletTip] = useState(false);
  const manualTipCents = Math.max(
    0,
    Math.round((parseFloat(manualTipStr) || 0) * 100)
  );
  const effectiveTipCents =
    tipMode === "manual"
      ? manualTipCents
      : tipMode === "tablet"
      ? (tabletTipCents || 0)
      : 0;
  const chargeCents = Math.round(total * 100) + effectiveTipCents;

  // Detect mobile platform — Square POS deep-link only works on iOS/Android.
  // (getSquarePlatform handles iPadOS-in-desktop-mode reporting a Mac UA.)
  // `forceTablet` is a manual override for devices that still misreport; the
  // state only exists to re-render after we flip the localStorage flag.
  const [forceTablet, setForceTablet] = useState(() => {
    try { return localStorage.getItem("bk_pos_force_tablet") || "off"; } catch { return "off"; }
  });
  const enableTablet = (kind) => {
    try { localStorage.setItem("bk_pos_force_tablet", kind); } catch { /* ignore */ }
    setForceTablet(kind);
  };
  const resetTablet = () => {
    try { localStorage.removeItem("bk_pos_force_tablet"); } catch { /* ignore */ }
    setForceTablet("off");
  };
  const { isAndroid, isIOS, isMobile } = getSquarePlatform();

  // Poll for callback completion once we've launched into Square POS.
  useEffect(() => {
    if (!saleId || !polling) return;
    let cancelled = false;
    const t = setInterval(async () => {
      try {
        const { data } = await adminApi(token).get(
          `/admin/pos/square-sale/${saleId}`
        );
        if (cancelled) return;
        if (data.status === "PAID") {
          setPolling(false);
          onSuccess({
            order_number: data.order_number,
            total: chargeCents / 100,
            status: payload.send_to_kitchen ? "Received" : "Completed",
          });
        } else if (data.status === "OFFLINE_PENDING") {
          // Reader took the card while the tablet was offline. The order is
          // already on the KDS; Square confirms the money once back online.
          setPolling(false);
          toast.success("Paid offline — order sent to kitchen. Will auto-confirm when back online.");
          onSuccess({
            order_number: data.order_number,
            total: chargeCents / 100,
            status: "Received",
            offline: true,
          });
        } else if (data.status === "FAILED") {
          setPolling(false);
          setError("Payment failed or cancelled on the reader. Try again.");
        }
      } catch {
        /* transient */
      }
    }, 2500);
    return () => { cancelled = true; clearInterval(t); };
  }, [saleId, polling, token, chargeCents, onSuccess, payload.send_to_kitchen]);

  // ── Pre-create the Square sale so the cashier's tap can hand off to Square
  // synchronously (iOS Safari drops app-switches that happen after an await).
  const tipReady = tipMode !== "tablet" || tabletTipCents !== null;
  const noteText = payload.customer_name || "Blossom's Kitchen POS";
  const prepKey =
    method === "card" && isMobile && tipReady && !polling
      ? JSON.stringify({ payload, tip: effectiveTipCents, isAndroid, isIOS })
      : "";
  const [prepared, setPrepared] = useState(null); // { key, sale_id, url, amount_cents }
  const [preparing, setPreparing] = useState(false);
  const preparedRef = useRef(null);
  const consumedRef = useRef(false);
  useEffect(() => { preparedRef.current = prepared; }, [prepared]);

  const discardPrepared = (p) => {
    if (!p?.sale_id) return;
    adminApi(token).delete(`/admin/pos/square-sale/${p.sale_id}`).catch(() => {});
  };

  useEffect(() => {
    if (!prepKey) return;
    if (preparedRef.current?.key === prepKey) return;
    let cancelled = false;
    setPreparing(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await adminApi(token).post("/admin/pos/square-sale", {
          ...payload,
          tip_cents: effectiveTipCents,
        });
        if (cancelled) { discardPrepared({ sale_id: data.sale_id }); return; }
        const url = buildSquareUrl(data, noteText, { isAndroid, isIOS });
        setPrepared((prev) => {
          if (prev && prev.key !== prepKey) discardPrepared(prev);
          return { key: prepKey, sale_id: data.sale_id, url, amount_cents: data.amount_cents };
        });
        setError("");
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.detail || "Couldn't prepare sale");
      } finally {
        if (!cancelled) setPreparing(false);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prepKey, token]);

  // Throw away an unused pre-created sale when the dialog closes.
  useEffect(() => () => {
    if (!consumedRef.current) discardPrepared(preparedRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const launchSquarePos = async () => {
    setError("");
    const ready = prepared && prepared.key === prepKey && prepared.url;
    if (ready) {
      // Fast path: everything was built ahead of time — hand off inside the tap.
      consumedRef.current = true;
      setSaleId(prepared.sale_id);
      setSquareUrl(prepared.url);
      setSquareOpened(false);
      setPolling(true);
      window.location.href = prepared.url;
      return;
    }
    if (!isMobile) {
      setError("Open this on your iPad or Android tablet with the Square POS app installed.");
      return;
    }
    // Slow path (pre-creation still in flight or failed): create now, then launch.
    setStarting(true);
    try {
      const { data } = await adminApi(token).post("/admin/pos/square-sale", {
        ...payload,
        tip_cents: effectiveTipCents,
      });
      consumedRef.current = true;
      setSaleId(data.sale_id);
      const url = buildSquareUrl(data, noteText, { isAndroid, isIOS });
      setSquareUrl(url);
      setSquareOpened(false);
      setPolling(true);
      window.location.href = url;
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't start sale");
    } finally {
      setStarting(false);
    }
  };

  // Customer-facing tip screen is shown when the cashier presses
  // "Hand to Customer" in tablet mode. The full-screen picker uses
  // `subtotalCents` so the customer sees exactly what they're tipping ON.
  const subtotalCents = Math.round(total * 100);

  // Cash flow — no Square tip prompt; charge the order total as-is.
  const cashDueCents = Math.round(total * 100);
  const tenderedCents = Math.max(0, Math.round((parseFloat(tenderedStr) || 0) * 100));
  const changeCents = tenderedCents - cashDueCents;
  const recordCash = async () => {
    setCashSaving(true);
    setError("");
    try {
      const { data } = await adminApi(token).post("/admin/pos/cash-sale", {
        ...payload,
        tip_cents: 0,
      });
      onSuccess({ order_number: data.order_number, total: data.total, status: "Received" });
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't record cash sale");
    } finally {
      setCashSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-brand-red text-2xl">
            Charge ${(chargeCents / 100).toFixed(2)}
          </h2>
          <button onClick={onClose}><X className="w-5 h-5 text-stone-500" /></button>
        </div>

        {/* Payment method toggle */}
        <div className="grid grid-cols-2 gap-2" data-testid="pos-method-toggle">
          <button
            type="button"
            onClick={() => setMethod("card")}
            data-testid="pos-method-card"
            className={`py-2.5 rounded-lg border-2 font-black uppercase tracking-widest text-[11px] flex items-center justify-center gap-1.5 ${
              method === "card" ? "border-brand-red bg-brand-red text-white" : "border-stone-300 text-stone-500 hover:border-brand-red"
            }`}
          >
            <CreditCard className="w-4 h-4" /> Card reader
          </button>
          <button
            type="button"
            onClick={() => setMethod("cash")}
            data-testid="pos-method-cash"
            className={`py-2.5 rounded-lg border-2 font-black uppercase tracking-widest text-[11px] flex items-center justify-center gap-1.5 ${
              method === "cash" ? "border-emerald-600 bg-emerald-600 text-white" : "border-stone-300 text-stone-500 hover:border-emerald-600"
            }`}
          >
            <DollarSign className="w-4 h-4" /> Cash
          </button>
        </div>

        {method === "card" ? (
        <>
        <div className="rounded-xl bg-stone-50 border border-stone-200 p-4 text-sm text-stone-700 space-y-2">
          <div className="font-black text-brand-red text-xs uppercase tracking-widest">
            Bluetooth reader payment
          </div>
          <div>
            Tapping <b>Open Square POS</b> hands this sale to the Square app,
            where the customer taps / inserts their card on your paired reader.
            You'll be returned here automatically.
          </div>
          {!isMobile ? (
            <div className="rounded-xl border-2 border-brand-red/40 bg-brand-red/5 p-3 space-y-2" data-testid="pos-tablet-warning">
              <div className="text-brand-red font-bold text-sm">
                ⚠ This device looks like a desktop browser, which can't reach the Bluetooth reader. If you're actually on the iPad (or Android tablet) that the Square app is signed in on, turn on tablet mode:
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => enableTablet("ios")}
                  data-testid="pos-force-tablet-ios"
                  className="px-3 py-2 rounded-lg bg-brand-red text-white text-[11px] uppercase tracking-widest font-black hover:bg-red-800"
                >
                  I'm on an iPad — enable reader
                </button>
                <button
                  type="button"
                  onClick={() => enableTablet("android")}
                  data-testid="pos-force-tablet-android"
                  className="px-3 py-2 rounded-lg border-2 border-brand-red text-brand-red text-[11px] uppercase tracking-widest font-black hover:bg-brand-red/10"
                >
                  Android tablet
                </button>
              </div>
            </div>
          ) : forceTablet !== "off" ? (
            <div className="text-[11px] text-stone-500 flex items-center gap-2" data-testid="pos-tablet-override-on">
              <Check className="w-3.5 h-3.5 text-emerald-600" />
              Tablet mode is on ({forceTablet === "android" ? "Android" : "iPad"}) — reader hand-off enabled.
              <button
                type="button"
                onClick={resetTablet}
                data-testid="pos-tablet-override-reset"
                className="underline font-bold hover:text-brand-red"
              >
                Reset
              </button>
            </div>
          ) : null}
          <details className="text-[11px] text-stone-500" data-testid="pos-square-setup">
            <summary className="cursor-pointer font-bold hover:text-brand-red">Square setup (callback URL)</summary>
            <SquareSetupInfo token={token} />
          </details>
        </div>

        {/* Tip flow — three modes */}
        <div className="rounded-xl border border-stone-200 p-3 space-y-2" data-testid="pos-tip-panel">
          <div className="font-black text-stone-700 text-[11px] uppercase tracking-widest">
            Tip
          </div>
          <div className="grid grid-cols-3 gap-2 text-sm">
            <button
              type="button"
              onClick={() => { setTipMode("tablet"); setTabletTipCents(null); setShowTabletTip(false); }}
              data-testid="pos-tip-mode-tablet"
              className={`py-2 rounded-lg border-2 font-bold uppercase tracking-widest text-[10px] ${
                tipMode === "tablet"
                  ? "border-brand-red bg-brand-red text-white"
                  : "border-stone-300 text-stone-500 hover:border-brand-red"
              }`}
            >
              Ask on tablet
            </button>
            <button
              type="button"
              onClick={() => setTipMode("reader")}
              data-testid="pos-tip-mode-reader"
              className={`py-2 rounded-lg border-2 font-bold uppercase tracking-widest text-[10px] ${
                tipMode === "reader"
                  ? "border-brand-red bg-brand-red text-white"
                  : "border-stone-300 text-stone-500 hover:border-brand-red"
              }`}
            >
              Ask on reader
            </button>
            <button
              type="button"
              onClick={() => setTipMode("manual")}
              data-testid="pos-tip-mode-manual"
              className={`py-2 rounded-lg border-2 font-bold uppercase tracking-widest text-[10px] ${
                tipMode === "manual"
                  ? "border-brand-red bg-brand-red text-white"
                  : "border-stone-300 text-stone-500 hover:border-brand-red"
              }`}
            >
              Enter manually
            </button>
          </div>
          {tipMode === "tablet" && (
            <div className="text-[11px] text-stone-500 leading-snug">
              Tap <b>Hand to Customer</b> below — the tablet shows a big tip
              picker for them. Once they choose, the tip is baked into the
              amount sent to the Square reader.
              {tabletTipCents !== null && (
                <div className="mt-1 text-emerald-600 font-bold" data-testid="pos-tablet-tip-selected">
                  ✓ Customer picked ${(tabletTipCents / 100).toFixed(2)} tip
                </div>
              )}
            </div>
          )}
          {tipMode === "reader" && (
            <div className="text-[11px] text-stone-500 leading-snug">
              Square's tip screen will show on the chip reader after the sale
              is sent. Enable it in Square Register → Settings → Tips → <b>Collect tips on card reader</b>.
            </div>
          )}
          {tipMode === "manual" && (
            <div className="space-y-1">
              <input
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                value={manualTipStr}
                onChange={(e) => setManualTipStr(e.target.value)}
                placeholder="Tip $"
                data-testid="pos-manual-tip-input"
                className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-red outline-none text-sm font-mono"
              />
              <div className="text-[11px] text-stone-500">
                Baked into the amount sent to Square — reader will NOT prompt again.
              </div>
            </div>
          )}
        </div>

        {error && (
          <div className="text-brand-red text-sm bg-red-50 border border-red-200 rounded p-2">
            {error}
          </div>
        )}

        {polling ? (
          <div className="space-y-3 py-2" data-testid="pos-waiting-square">
            <div className="text-center text-stone-500 text-sm">
              Waiting for Square POS to complete the payment…
            </div>
            {!squareOpened && squareUrl && (
              <>
                <a
                  href={squareUrl}
                  data-testid="pos-open-square-manual-btn"
                  className="block w-full text-center py-3.5 rounded-lg bg-brand-red text-white font-black text-sm uppercase tracking-widest hover:bg-red-800 active:scale-[0.98]"
                >
                  Open Square POS
                </a>
                <div className="text-[11px] text-stone-500 text-center leading-snug">
                  Square didn't open? Tap the button above — iPad Safari needs a direct tap to switch apps.
                </div>
              </>
            )}
          </div>
        ) : tipMode === "tablet" && tabletTipCents === null ? (
          <button
            onClick={() => setShowTabletTip(true)}
            data-testid="pos-hand-to-customer-btn"
            className="w-full py-3 rounded-lg bg-brand-red text-white font-black text-sm uppercase tracking-widest hover:bg-red-800"
          >
            Hand to Customer →
          </button>
        ) : prepared && prepared.key === prepKey && prepared.url ? (
          <a
            href={prepared.url}
            onClick={() => {
              consumedRef.current = true;
              setError("");
              setSaleId(prepared.sale_id);
              setSquareUrl(prepared.url);
              setSquareOpened(false);
              setPolling(true);
            }}
            data-testid="pos-confirm-charge-btn"
            className="block w-full text-center py-3 rounded-lg bg-brand-red text-white font-black text-sm uppercase tracking-widest hover:bg-red-800 active:scale-[0.98]"
          >
            Open Square POS · ${(prepared.amount_cents / 100).toFixed(2)}
          </a>
        ) : (
          <button
            onClick={launchSquarePos}
            disabled={starting || !isMobile}
            data-testid="pos-confirm-charge-btn"
            className="w-full py-3 rounded-lg bg-brand-red text-white font-black text-sm uppercase tracking-widest disabled:opacity-40 hover:bg-red-800"
          >
            {starting ? "Starting…" : preparing ? "Preparing sale…" : `Open Square POS · $${(chargeCents / 100).toFixed(2)}`}
          </button>
        )}
        </>
        ) : (
          /* ─── Cash tender ─── */
          <div className="space-y-3" data-testid="pos-cash-panel">
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 text-sm text-stone-700 space-y-1">
              <div className="font-black text-emerald-700 text-xs uppercase tracking-widest">
                Cash payment
              </div>
              <div className="flex justify-between font-mono">
                <span>Total due</span>
                <span className="font-black" data-testid="pos-cash-due">${(cashDueCents / 100).toFixed(2)}</span>
              </div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest font-black text-stone-600">
                Amount tendered
              </label>
              <div className="mt-1 flex items-center gap-2">
                <span className="text-stone-500 font-bold text-lg">$</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  value={tenderedStr}
                  onChange={(e) => setTenderedStr(e.target.value)}
                  placeholder={(cashDueCents / 100).toFixed(2)}
                  data-testid="pos-cash-tendered-input"
                  className="flex-1 px-3 py-2.5 rounded-lg border border-stone-300 focus:border-emerald-600 outline-none text-lg font-mono"
                  autoFocus
                />
              </div>
              {/* Quick-tender chips */}
              <div className="flex gap-2 mt-2">
                {[cashDueCents, Math.ceil(cashDueCents / 500) * 500, Math.ceil(cashDueCents / 2000) * 2000].map((c, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setTenderedStr((c / 100).toFixed(2))}
                    data-testid={`pos-cash-quick-${i}`}
                    className="flex-1 py-1.5 rounded-lg border border-stone-300 text-xs font-bold font-mono text-stone-700 hover:border-emerald-600 hover:text-emerald-700"
                  >
                    ${(c / 100).toFixed(2)}
                  </button>
                ))}
              </div>
            </div>
            {tenderedCents > 0 && (
              <div
                className={`rounded-xl p-4 text-center ${changeCents >= 0 ? "bg-stone-900 text-brand-yellow" : "bg-red-50 border border-red-200 text-brand-red"}`}
                data-testid="pos-cash-change"
              >
                <div className="text-[10px] uppercase tracking-widest font-black opacity-80">
                  {changeCents >= 0 ? "Change due" : "Still owed"}
                </div>
                <div className="font-display text-4xl mt-0.5 font-mono">
                  ${Math.abs(changeCents / 100).toFixed(2)}
                </div>
              </div>
            )}
            {error && (
              <div className="text-brand-red text-sm bg-red-50 border border-red-200 rounded p-2">
                {error}
              </div>
            )}
            <button
              onClick={recordCash}
              disabled={cashSaving || tenderedCents < cashDueCents}
              data-testid="pos-cash-confirm-btn"
              className="w-full py-3 rounded-lg bg-emerald-600 text-white font-black text-sm uppercase tracking-widest disabled:opacity-40 hover:bg-emerald-700"
            >
              {cashSaving ? "Recording…" : `Record cash sale · $${(cashDueCents / 100).toFixed(2)}`}
            </button>
          </div>
        )}
      </div>

      {/* Customer-facing full-screen tip picker */}
      {tipMode === "tablet" && showTabletTip && (
        <CustomerTipScreen
          subtotalCents={subtotalCents}
          onPick={(cents) => { setTabletTipCents(cents); setShowTabletTip(false); }}
          onCancel={() => setShowTabletTip(false)}
        />
      )}
    </div>
  );
}


/**
 * CustomerTipScreen — full-screen, high-contrast, customer-facing picker
 * shown after the cashier hands the tablet over. Three % buttons, a custom
 * $ input, and a "No Tip" option. Shows the sale total so the customer
 * knows exactly what they're tipping ON and what they'll be charged.
 */
function CustomerTipScreen({ subtotalCents, onPick, onCancel }) {
  const [customStr, setCustomStr] = useState("");
  const pct = (p) => Math.round((subtotalCents * p) / 100);
  const options = [
    { label: "15%", pct: 15 },
    { label: "18%", pct: 18 },
    { label: "20%", pct: 20 },
  ];
  const fmt = (cents) => `$${(cents / 100).toFixed(2)}`;
  const submitCustom = () => {
    const cents = Math.max(0, Math.round((parseFloat(customStr) || 0) * 100));
    onPick(cents);
  };
  return (
    <div
      className="fixed inset-0 z-[60] bg-white flex flex-col items-center justify-center p-6 sm:p-10"
      data-testid="customer-tip-screen"
    >
      <div className="w-full max-w-2xl space-y-6">
        <div className="text-center space-y-1">
          <div className="font-hand text-brand-red text-4xl sm:text-5xl">
            Add a tip?
          </div>
          <div className="text-stone-500 text-sm uppercase tracking-widest">
            100% goes to the crew
          </div>
        </div>

        <div className="rounded-2xl bg-stone-50 border-2 border-stone-200 p-6 text-center">
          <div className="text-xs uppercase tracking-widest text-stone-500 font-bold">
            Your total before tip
          </div>
          <div className="font-display text-brand-red text-5xl sm:text-6xl mt-1">
            {fmt(subtotalCents)}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          {options.map((o) => {
            const cents = pct(o.pct);
            return (
              <button
                key={o.pct}
                onClick={() => onPick(cents)}
                data-testid={`customer-tip-${o.pct}`}
                className="py-6 rounded-2xl border-4 border-brand-red bg-white hover:bg-brand-red hover:text-white transition-colors"
              >
                <div className="font-display text-brand-red text-4xl group-hover:text-white">
                  {o.label}
                </div>
                <div className="text-xs uppercase tracking-widest text-stone-500 font-bold mt-1">
                  {fmt(cents)}
                </div>
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="rounded-2xl border-2 border-stone-300 p-4 flex items-center gap-2 min-w-0 overflow-hidden">
            <span className="text-stone-500 font-bold shrink-0">Custom $</span>
            <input
              type="number"
              min="0"
              step="0.01"
              inputMode="decimal"
              value={customStr}
              onChange={(e) => setCustomStr(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submitCustom(); }}
              placeholder="0.00"
              data-testid="customer-tip-custom-input"
              className="min-w-0 flex-1 px-2 py-2 outline-none text-xl font-mono text-right"
            />
            <button
              onClick={submitCustom}
              data-testid="customer-tip-custom-submit"
              className="shrink-0 px-4 py-2 rounded-lg bg-brand-red text-white font-bold uppercase tracking-widest text-xs relative z-10"
            >
              OK
            </button>
          </div>
          <button
            onClick={() => onPick(0)}
            data-testid="customer-tip-none"
            className="py-4 rounded-2xl border-2 border-stone-300 text-stone-500 font-black uppercase tracking-widest text-sm hover:bg-stone-100"
          >
            No Tip
          </button>
        </div>

        <div className="text-center">
          <button
            onClick={onCancel}
            data-testid="customer-tip-back"
            className="text-stone-400 text-sm underline"
          >
            ← Back to cashier
          </button>
        </div>
      </div>
    </div>
  );
}

function ReceiptDialog({ receipt, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div
        data-testid="pos-receipt"
        className="bg-white rounded-2xl w-full max-w-sm p-5 space-y-3 text-center"
      >
        <div className="text-emerald-600 text-5xl">✓</div>
        <div className="font-hand text-brand-red text-3xl leading-tight">
          Paid!
        </div>
        <div className="text-stone-500 text-sm">
          Order #{receipt.order_number} · ${receipt.total.toFixed(2)}
        </div>
        {receipt.status === "Received" && (
          <div className="text-xs text-brand-red font-bold uppercase tracking-widest">
            Sent to kitchen
          </div>
        )}
        <button
          onClick={onClose}
          data-testid="pos-new-sale-btn"
          className="w-full py-3 rounded-lg bg-brand-red text-white font-black text-sm uppercase tracking-widest hover:bg-red-800"
        >
          New sale
        </button>
      </div>
    </div>
  );
}

function CashDrawerSection({ token, date, cd, onSaved }) {
  const [openingFloat, setOpeningFloat] = useState("");
  const [counted, setCounted] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setOpeningFloat(cd?.opening_float ? String(cd.opening_float) : "");
    setCounted(cd?.counted_cash != null ? String(cd.counted_cash) : "");
  }, [cd?.opening_float, cd?.counted_cash, date]);
  const fmt = (n) => `$${(Number(n) || 0).toFixed(2)}`;
  const openingNum = parseFloat(openingFloat) || 0;
  const expected = Math.round((openingNum + (cd?.cash_sales || 0)) * 100) / 100;
  const countedNum = counted === "" ? null : parseFloat(counted) || 0;
  const overShort = countedNum == null ? null : Math.round((countedNum - expected) * 100) / 100;
  const save = async () => {
    setSaving(true);
    try {
      await adminApi(token).post("/admin/pos/cash-drawer", {
        date,
        opening_float: openingNum,
        counted_cash: counted === "" ? undefined : countedNum,
      });
      toast.success("Cash drawer saved");
      onSaved?.();
    } catch {
      toast.error("Couldn't save cash drawer");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-3" data-testid="pos-cash-drawer-section">
      <div className="text-[10px] uppercase tracking-widest text-emerald-800 font-black mb-2">
        Cash drawer — count at close
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
        <div className="rounded-xl bg-white border border-stone-200 p-2">
          <div className="text-[9px] uppercase tracking-widest text-stone-500">Opening float</div>
          <div className="flex items-center justify-center gap-1 mt-1">
            <span className="text-stone-400">$</span>
            <input
              type="number" min="0" step="0.01" inputMode="decimal"
              value={openingFloat}
              onChange={(e) => setOpeningFloat(e.target.value)}
              placeholder="0.00"
              data-testid="cash-opening-float-input"
              className="w-20 text-center font-mono text-lg outline-none border-b border-stone-300 focus:border-emerald-600"
            />
          </div>
        </div>
        <StatTile label="Cash sales" value={fmt(cd?.cash_sales)} />
        <div className="rounded-xl bg-stone-900 text-brand-yellow p-2" data-testid="cash-expected">
          <div className="text-[9px] uppercase tracking-widest opacity-80">Expected in drawer</div>
          <div className="font-mono text-lg font-black mt-1">{fmt(expected)}</div>
        </div>
        <div className="rounded-xl bg-white border border-stone-200 p-2">
          <div className="text-[9px] uppercase tracking-widest text-stone-500">Counted cash</div>
          <div className="flex items-center justify-center gap-1 mt-1">
            <span className="text-stone-400">$</span>
            <input
              type="number" min="0" step="0.01" inputMode="decimal"
              value={counted}
              onChange={(e) => setCounted(e.target.value)}
              placeholder="0.00"
              data-testid="cash-counted-input"
              className="w-20 text-center font-mono text-lg outline-none border-b border-stone-300 focus:border-emerald-600"
            />
          </div>
        </div>
      </div>
      {overShort != null && (
        <div className="mt-3 flex items-center justify-center">
          <div
            data-testid="cash-over-short"
            className={`rounded-xl px-5 py-2 text-center ${
              overShort === 0 ? "bg-stone-900 text-brand-yellow"
              : overShort > 0 ? "bg-emerald-600 text-white"
              : "bg-red-600 text-white"
            }`}
          >
            <div className="text-[10px] uppercase tracking-widest font-black opacity-80">
              {overShort === 0 ? "Balanced ✓" : overShort > 0 ? "Over" : "Short"}
            </div>
            <div className="font-display text-3xl font-mono">
              {overShort < 0 ? "−" : ""}{fmt(Math.abs(overShort))}
            </div>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between gap-2 mt-3 no-print">
        <div className="text-[11px] text-stone-500">
          Expected = opening float + cash sales. Count the drawer at close and enter it.
        </div>
        <button
          onClick={save}
          disabled={saving}
          data-testid="cash-drawer-save-btn"
          className="shrink-0 px-4 py-2 rounded-full bg-emerald-600 text-white text-[11px] uppercase tracking-widest font-black hover:bg-emerald-700 disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save count"}
        </button>
      </div>
    </div>
  );
}

/**
 * ShiftReportDialog — end-of-shift report for a given date. Defaults to
 * today. Shows totals, channel breakdown, top-selling items, and an
 * hour-by-hour view. Print button uses `window.print()` with a scoped
 * print stylesheet (same trick as the temp log).
 */
function ShiftReportDialog({ token, onClose }) {
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (d) => {
    setLoading(true);
    try {
      const { data } = await adminApi(token).get("/admin/pos/report", {
        params: { date: d },
      });
      setReport(data);
    } catch {
      toast.error("Couldn't load report");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(date); /* eslint-disable-next-line */ }, [date]);

  const fmt = (n) => `$${(n || 0).toFixed(2)}`;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          .shift-report-print, .shift-report-print * { visibility: visible !important; }
          .shift-report-print {
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            width: 100% !important;
            padding: 20px !important;
            background: white !important;
            color: black !important;
          }
          .no-print { display: none !important; }
        }
      `}</style>
      <div
        data-testid="pos-shift-report"
        className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <div className="chalkboard-band px-4 py-3 flex items-center justify-between no-print">
          <div>
            <div className="text-brand-yellow font-display text-xl leading-tight">
              End-of-shift Report
            </div>
            <div className="text-[11px] text-white/80">
              Everything sold on this date (UTC).
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              data-testid="pos-shift-date"
              className="px-2 py-1 rounded text-stone-800 text-sm"
            />
            <button
              onClick={async () => {
                try {
                  await adminApi(token).post("/admin/pos/email-report", { date });
                  toast.success("Report emailed");
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Email failed");
                }
              }}
              data-testid="pos-shift-email-btn"
              className="px-3 py-1.5 rounded-full bg-white text-stone-800 text-[11px] uppercase tracking-widest font-bold hover:bg-brand-yellow"
              title="Email this report to the owner"
            >
              Email
            </button>
            <CloseShiftButton token={token} compact />
            <button
              onClick={() => window.print()}
              data-testid="pos-shift-print-btn"
              className="px-3 py-1.5 rounded-full bg-white text-stone-800 text-[11px] uppercase tracking-widest font-bold flex items-center gap-1 hover:bg-brand-yellow"
            >
              <Printer className="w-3.5 h-3.5" />
              Print
            </button>
            <button
              onClick={onClose}
              className="p-1 text-white/80 hover:text-white"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-4 shift-report-print" style={{ fontFamily: "sans-serif" }}>
          <h2 style={{ fontSize: 22, margin: "0 0 4px" }}>
            Blossom's Kitchen — Shift Report
          </h2>
          <div style={{ fontSize: 13, marginBottom: 16 }}>
            Date: <strong>{date}</strong>
          </div>

          {loading ? (
            <div className="text-center text-stone-400 py-8">Loading…</div>
          ) : !report ? null : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
                <StatTile label="App orders" value={report.order_count} />
                <StatTile label="App gross" value={fmt(report.gross)} />
                <StatTile label="Labor" value={fmt(report.labor_cost)} />
                <StatTile label="Net" value={fmt((report.gross || 0) - (report.labor_cost || 0))} />
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center mt-3">
                <StatTile label="Tax" value={fmt(report.tax)} />
                <StatTile label="Discounts" value={`− ${fmt(report.discount)}`} />
                <StatTile label="Labor hrs" value={(report.labor_hours || 0).toFixed(2)} />
                <StatTile label="Tips" value={fmt(report.tip)} />
              </div>

              <div className="mt-4 rounded-2xl border border-sky-200 bg-sky-50 p-3" data-testid="pos-shift-online-section">
                <div className="text-[10px] uppercase tracking-widest text-sky-800 font-black mb-2">Online sales (ordered through the app)</div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
                  <StatTile label="Online orders" value={report.by_channel?.online?.count ?? 0} />
                  <StatTile label="Online sales" value={fmt(report.by_channel?.online?.gross)} />
                  <StatTile label="Paid by card" value={fmt(report.online_by_payment?.card?.gross)} />
                  <StatTile label="Paid cash at truck" value={fmt(report.online_by_payment?.cash?.gross)} />
                </div>
                {report.cancelled?.count > 0 && (
                  <div className="text-[11px] text-stone-500 mt-2">Cancelled (not counted): {report.cancelled.count} · {fmt(report.cancelled.gross)}</div>
                )}
              </div>

              {report.cash_drawer && (
                <CashDrawerSection
                  token={token}
                  date={date}
                  cd={report.cash_drawer}
                  onSaved={() => load(date)}
                />
              )}

              <SquareRegisterSection square={report.square} />

              <h3 style={{ fontSize: 14, margin: "20px 0 6px" }}>
                App orders by channel
              </h3>
              <table className="w-full text-sm border-collapse" style={{ borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#f5f5f4" }}>
                    <th style={cellHead}>Channel</th>
                    <th style={cellHead}>Sales</th>
                    <th style={cellHead}>Gross</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={cell}>In-truck POS (card)</td>
                    <td style={cell}>{report.by_channel.pos.count}</td>
                    <td style={cell}>{fmt(report.by_channel.pos.gross)}</td>
                  </tr>
                  <tr>
                    <td style={cell}>Online orders</td>
                    <td style={cell}>{report.by_channel.online.count}</td>
                    <td style={cell}>{fmt(report.by_channel.online.gross)}</td>
                  </tr>
                </tbody>
              </table>

              {true && (
                <>
                  <h3 style={{ fontSize: 14, margin: "20px 0 6px" }}>
                    Labor for the day
                  </h3>
                  <table className="w-full text-sm" style={{ borderCollapse: "collapse" }} data-testid="pos-shift-labor-table">
                    <thead>
                      <tr style={{ background: "#f5f5f4" }}>
                        <th style={cellHead}>Employee</th>
                        <th style={cellHead}>Hours</th>
                        <th style={cellHead}>Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!report.labor_by_employee?.length && (
                        <tr><td colSpan={3} style={{ ...cell, textAlign: "center", color: "#888" }}>No clock-ins recorded.</td></tr>
                      )}
                      {(report.labor_by_employee || []).map((r, i) => (
                        <tr key={i}>
                          <td style={cell}>{r.name}</td>
                          <td style={cell}>{(r.hours || 0).toFixed(2)}</td>
                          <td style={cell}>{fmt(r.cost)}</td>
                        </tr>
                      ))}
                      <tr style={{ background: "#fafaf9", fontWeight: 700 }}>
                        <td style={cell}>Total</td>
                        <td style={cell}>{(report.labor_hours || 0).toFixed(2)}</td>
                        <td style={cell}>{fmt(report.labor_cost)}</td>
                      </tr>
                    </tbody>
                  </table>
                </>
              )}

              {report.by_item.length > 0 && (
                <>
                  <h3 style={{ fontSize: 14, margin: "20px 0 6px" }}>
                    Top items ({report.by_item.length})
                  </h3>
                  <table className="w-full text-sm" style={{ borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ background: "#f5f5f4" }}>
                        <th style={cellHead}>Item</th>
                        <th style={cellHead}>Qty</th>
                        <th style={cellHead}>Gross</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.by_item.map((r, i) => (
                        <tr key={i}>
                          <td style={cell}>{r.name}</td>
                          <td style={cell}>{r.qty}</td>
                          <td style={cell}>{fmt(r.gross)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}

              {report.by_hour.length > 0 && (
                <>
                  <h3 style={{ fontSize: 14, margin: "20px 0 6px" }}>
                    Hour-by-hour
                  </h3>
                  <table className="w-full text-sm" style={{ borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ background: "#f5f5f4" }}>
                        <th style={cellHead}>Hour ({report.timezone || "local"})</th>
                        <th style={cellHead}>Sales</th>
                        <th style={cellHead}>Gross</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.by_hour.map((r) => (
                        <tr key={r.hour}>
                          <td style={cell}>{String(r.hour).padStart(2, "0")}:00</td>
                          <td style={cell}>{r.count}</td>
                          <td style={cell}>{fmt(r.gross)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}

              {report.order_count === 0 && (
                <div className="text-center text-stone-400 py-6 text-sm">
                  No sales recorded on this date.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const cell = {
  border: "1px solid #d6d3d1",
  padding: "6px 10px",
  fontSize: 13,
};
const cellHead = { ...cell, background: "#f5f5f4", textAlign: "left", fontWeight: 700 };

function StatTile({ label, value }) {
  return (
    <div className="bg-stone-50 border border-stone-200 rounded-xl p-3">
      <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">
        {label}
      </div>
      <div className="font-display text-brand-red text-2xl font-black mt-1">
        {value}
      </div>
    </div>
  );
}



/**
 * ClockDialog — 4-digit PIN pad shown on the POS login screen so staff can
 * clock in/out without needing the admin passcode. Toggles state on the
 * server: open shift -> clock out; no open shift -> clock in.
 */
function ClockDialog({ onClose }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (pin.length !== 4) return;
    setBusy(true);
    try {
      const { data } = await api.post("/pos/clock-toggle", { pin });
      setResult(data);
      const msg = data.action === "in"
        ? `Welcome, ${data.employee_name} — Clocked in`
        : `Bye, ${data.employee_name} — ${data.hours}h logged`;
      toast.success(msg, { duration: 2500 });
      setTimeout(onClose, 1500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "PIN not recognised");
      setPin("");
    } finally {
      setBusy(false);
    }
  };

  const press = (n) => {
    setPin((p) => (p.length >= 4 ? p : p + n));
  };
  const back = () => setPin((p) => p.slice(0, -1));

  useEffect(() => {
    if (pin.length === 4 && !busy) submit();
    // eslint-disable-next-line
  }, [pin]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="pos-clock-dialog"
    >
      <div
        className="bg-white rounded-2xl w-full max-w-xs p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="font-display text-brand-red text-2xl leading-none">
              Clock In / Out
            </div>
            <div className="text-[11px] text-stone-500">Enter your 4-digit PIN</div>
          </div>
          <button onClick={onClose} data-testid="pos-clock-close-btn">
            <X className="w-5 h-5 text-stone-500" />
          </button>
        </div>

        <div className="flex justify-center gap-2" data-testid="pos-clock-pin-dots">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className={`w-4 h-4 rounded-full border-2 transition-colors ${
                pin.length > i ? "bg-brand-red border-brand-red" : "border-stone-300"
              }`}
            />
          ))}
        </div>

        <div className="grid grid-cols-3 gap-2">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
            <button
              key={n}
              onClick={() => press(String(n))}
              disabled={busy}
              data-testid={`pos-clock-key-${n}`}
              className="py-4 rounded-xl bg-stone-100 hover:bg-stone-200 text-2xl font-black text-stone-800 disabled:opacity-40"
            >
              {n}
            </button>
          ))}
          <button
            onClick={back}
            disabled={busy}
            data-testid="pos-clock-key-back"
            className="py-4 rounded-xl bg-stone-100 hover:bg-stone-200 text-xs font-bold text-stone-600 uppercase tracking-widest disabled:opacity-40"
          >
            ⌫
          </button>
          <button
            onClick={() => press("0")}
            disabled={busy}
            data-testid="pos-clock-key-0"
            className="py-4 rounded-xl bg-stone-100 hover:bg-stone-200 text-2xl font-black text-stone-800 disabled:opacity-40"
          >
            0
          </button>
          <button
            onClick={() => setPin("")}
            disabled={busy}
            data-testid="pos-clock-key-clear"
            className="py-4 rounded-xl bg-stone-100 hover:bg-stone-200 text-xs font-bold text-stone-600 uppercase tracking-widest disabled:opacity-40"
          >
            Clear
          </button>
        </div>

        {result && (
          <div
            data-testid="pos-clock-result"
            className={`text-center text-sm font-bold ${
              result.action === "in" ? "text-emerald-600" : "text-brand-red"
            }`}
          >
            {result.action === "in"
              ? `✓ ${result.employee_name} clocked IN`
              : `✓ ${result.employee_name} clocked OUT · ${result.hours}h`}
          </div>
        )}
      </div>
    </div>
  );
}
